<template>
  <SPassengerInfo
    :isAuth="getIsAuth"
    :isFreeTrip="getIsFreeTrip"
    :customerDetails="getUserDetailsForBookingDetails"
    :passengerPhone="getPassengerPhone"
    :passengerFirstName="getPassengerFirstName"
    :passengerLastName="getPassengerLastName"
    :passengerEmail="getPassengerEmail"
    :isAnotherPassenger="isAnotherPassenger"
    :isRideToOrFromAirport="isRideToOrFromAirport"
    :flightDetails="getFlightDetails"
    :paymentType="getPaymentType"
    :paymentMethod="getPaymentMethod"
    :isOnBoardPaymentAllowed="isOnBoardPaymentAllowed"
    :commentForOutward="getCommentForOutward"
    :commentForReturn="getCommentForReturn"
    :tripType="getTripType"
    :appConfig="appConfig"
    :lang="this.$route.params.lang"
    :anotherPassengersList="getAnotherPassengerList"
    :opsZoneCountry="getCountry"
    :userLocation="getLocation"
    termsAndConditionsPageName="terms-and-conditions"
    privacyPolicyPageName="https://www.slg.lu/en/politique-de-confidentialite/"
    @updatePhone="updatePhone"
    @updateFirstName="updateFirstName"
    @updateLastName="updateLastName"
    @updateEmail="updateEmail"
    @updatePassenger="updatePassenger"
    @updateFlightDetails="updateFlightDetails"
    @updatePaymentType="updatePaymentType"
    @updatePaymentMethod="updatePaymentMethod"
    @updateCommentForOutward="updateCommentForOutward"
    @updateCommentForReturn="updateCommentForReturn"
    @populateUserProfileInfo="populateUserProfileInfo"
    @setAuthToken="setAuthToken"
    @setGlobalLoading="setGlobalLoading"
    @trackGtmEventForRegisteredUser="onTrackEventForRegisteredUser"
    @trackGtmEventForNonRegisteredUser="onTrackEventForNonRegisteredUser"
    @trackGtmEventForUserCreatedByOperator="onTrackEventForUserCreatedByOperator"
  >
    <template #anotherPassengerNightCard>
      <AnotherPassengerMainNightCard
        v-if="appConfig.isNightCardsSupport && isAnotherPassenger"
        ref="mainNightCard"
        :card="mainPassengerNightCard"
        :duplicate-cards="getMainPassengerDuplicateCards"
        :validation-result="mainPassengerValidationResult"
        :show-out-of-commune-disclaimer="showMainPassengerOutOfCommuneDisclaimer"
        @input="onMainPassengerNightCardInput"
        @blur="onMainPassengerNightCardBlur"
      />
    </template>
    <template #extendedCheckout>
      <NightCards
        ref="nightCards"
        :is-auth="getIsAuth"
        :is-another-passenger="isAnotherPassenger"
        :linked-night-rider-card-number="getNightRiderCardNumber"
        :account-phone-for-link="getUserDetailsForBookingDetails.phone"
        :info-link-url="nightCardProgramInfoUrl"
        :main-passenger-night-card="mainPassengerNightCard"
        :validate-main-passenger-night-card="validateMainPassengerNightCard"
        @update:mainPassengerNightCard="mainPassengerNightCard = $event"
        @updateNightCards="validNightCards => $emit('updateNightCards', validNightCards)"
        @night-card-linked="$emit('night-card-linked')"
      />
    </template>
  </SPassengerInfo>
</template>

<script lang="ts">
export default {
  name: 'PassengerInfo',
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import importedAppConfig from '@/appConfig';
import resolveNightCardProgramInfoUrl from '@/utils/resolveNightCardProgramInfoUrl';
import { isNightCardOutOfCommune } from '@/utils/nightCardCheckoutLogic';
import { SPassengerInfo } from '@slg/web-customer-shared';
import NightCards from './NightCards.vue';
import AnotherPassengerMainNightCard from './AnotherPassengerMainNightCard.vue';
import {
  useStore, useRouteRef, useI18n, useVm, useGlobalErrorDialog, useGtm,
} from '@/composables';

function createEmptyMainPassengerNightCard() {
  return {
    id: 'main-passenger-night-card',
    value: null as string | null,
    isFrontValid: false,
    isBackendValid: null as boolean | null,
    backendValidationResult: null as any,
  };
}

const emit = defineEmits(['onSignInSuccess', 'updateNightCards', 'night-card-linked']);
const store = useStore();
const route = useRouteRef();
const { i18n } = useI18n();
const vm = useVm();
const { setGlobalError } = useGlobalErrorDialog();
const { trackGtmEvent } = useGtm();

const appConfig = ref<Record<string, any>>({});
const mainPassengerNightCard = ref(createEmptyMainPassengerNightCard());
appConfig.value = importedAppConfig;

const getIsAuth = computed(() => store.getters['auth/getIsAuth']);
const getUserDetailsForBookingDetails = computed(() => store.getters['auth/getUserDetailsForBookingDetails']);
const getPassengerPhone = computed(() => store.getters['booking/getPassengerPhone']);
const getPassengerFirstName = computed(() => store.getters['booking/getPassengerFirstName']);
const getPassengerLastName = computed(() => store.getters['booking/getPassengerLastName']);
const getPassengerEmail = computed(() => store.getters['booking/getPassengerEmail']);
const isAnotherPassenger = computed(() => store.getters['booking/isAnotherPassenger']);
const isRideToOrFromAirport = computed(() => store.getters['booking/isRideToOrFromAirport']);
const getFlightDetails = computed(() => store.getters['booking/getFlightDetails']);
const getPaymentType = computed(() => store.getters['booking/getPaymentType']);
const getPaymentMethod = computed(() => store.getters['booking/getPaymentMethod']);
const isOnBoardPaymentAllowed = computed(() => store.getters['opsZone/isOnBoardPaymentAllowed']);
const getTripType = computed(() => store.getters['booking/getTripType']);
const getCommentForOutward = computed(() => store.getters['booking/getCommentForOutward']);
const getCommentForReturn = computed(() => store.getters['booking/getCommentForReturn']);
const getIsFreeTrip = computed(() => store.getters['booking/getIsFreeTrip']);
const getAnotherPassengerList = computed(() => store.getters['auth/getAnotherPassengerList']);
const getCountry = computed(() => store.getters['opsZone/getCountry']);
const getLocation = computed(() => store.getters['auth/getLocation']);
const getNightRiderCardNumber = computed(() => store.getters['auth/getNightRiderCardNumber']);
const nightCardProgramInfoUrl = computed(() => {
  const locale = route.value.params.lang || i18n.locale;
  return resolveNightCardProgramInfoUrl(
    importedAppConfig.nightCardProgramInfoUrl,
    locale as string,
    importedAppConfig,
  );
});
const getMainPassengerDuplicateCards = computed(() => (vm.$refs as any).nightCards?.getAdditionalCardsForDuplicateCheck?.() || []);
const mainPassengerValidationResult = computed(() => {
  if (!isAnotherPassenger.value || !appConfig.value.isNightCardsSupport) {
    return null;
  }
  const cardNumber = (mainPassengerNightCard.value.value || '').trim();
  if (!cardNumber) {
    return null;
  }
  return mainPassengerNightCard.value.backendValidationResult
    || (vm.$refs as any).nightCards?.getMainPassengerValidationResult?.()
    || null;
});
const showMainPassengerOutOfCommuneDisclaimer = computed(() => {
  if (!isAnotherPassenger.value || !appConfig.value.isNightCardsSupport) {
    return false;
  }
  const cardNumber = (mainPassengerNightCard.value.value || '').trim();
  if (!cardNumber) {
    return false;
  }
  const validationResult = mainPassengerValidationResult.value;
  return isNightCardOutOfCommune(validationResult);
});

watch(isAnotherPassenger, (isSelected) => {
  if (!isSelected) {
    resetMainPassengerNightCard();
  }
});

function resetMainPassengerNightCard() {
  mainPassengerNightCard.value = createEmptyMainPassengerNightCard();
}

function validateMainPassengerNightCard() {
  return (vm.$refs as any).mainNightCard?.validate?.() ?? Promise.resolve(true);
}

function onMainPassengerNightCardInput(value: string, valid: boolean) {
  const { nightCards } = vm.$refs as any;
  if (nightCards?.onMainPassengerNightCardInput) {
    nightCards.onMainPassengerNightCardInput(value, valid);
  }
}

function onMainPassengerNightCardBlur(valid: boolean) {
  const { nightCards } = vm.$refs as any;
  if (nightCards?.onMainPassengerNightCardBlur) {
    nightCards.onMainPassengerNightCardBlur(valid);
  }
}

function updatePhone(newValue: string) {
  if (!newValue?.includes('undefined')) {
    store.commit('booking/setPassengerPhone', newValue);
  }
}

function updateFirstName(newValue: string) {
  store.commit('booking/setPassengerFirstName', newValue);
}

function updateLastName(newValue: string) {
  store.commit('booking/setPassengerLastName', newValue);
}

function updateEmail(newValue: string) {
  store.commit('booking/setPassengerEmail', newValue);
}

function updatePassenger(newValue: boolean) {
  if (newValue) {
    store.commit('booking/setAnotherPassenger', newValue);
  } else {
    store.commit('booking/setLoggedInPassengerDetails', getUserDetailsForBookingDetails.value);
  }
}

function updateFlightDetails(newValue: string) {
  store.commit('booking/setFlightDetails', newValue);
}

function updatePaymentType(newValue: string) {
  store.commit('booking/setPaymentType', newValue);
}

function updatePaymentMethod(newValue: string) {
  store.commit('booking/setPaymentMethod', newValue);
}

function updateCommentForOutward(newValue: string) {
  store.commit('booking/setCommentForOutward', newValue);
}

function updateCommentForReturn(newValue: string) {
  store.commit('booking/setCommentForReturn', newValue);
}

function setGlobalLoading(isLoading: boolean) {
  store.commit('common/setGlobalLoading', isLoading);
}

function setAuthToken(authToken: string) {
  if (localStorage.getItem('authToken') !== authToken) {
    store.commit('auth/clearAuthTokenAndProfileInfo');
    store.commit('auth/setAuthToken', authToken);
  }
}

async function populateUserProfileInfo() {
  try {
    await store.dispatch('auth/populateUserProfileInfo');
    vm.$root.$emit('auth-success');
    emit('onSignInSuccess');
  } catch (e) {
    await setGlobalError(e as any);
  }
}

function onTrackEventForRegisteredUser() {
  trackGtmEvent('Checkout auth next button for registered user click', 'Button', 'Click', 'Next step for registered user');
}

function onTrackEventForNonRegisteredUser() {
  trackGtmEvent('Checkout auth next button for non registered user click', 'Button', 'Click', 'Next step for non-registered user');
}

function onTrackEventForUserCreatedByOperator() {
  trackGtmEvent(
    'Checkout auth next button for registered user created by operator click',
    'Button',
    'Click',
    'Next step for created by operator user',
  );
}
</script>
